import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getFeed, togglePostLike } from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import useUserStore from "@store/useUserStore";
import PostContentPreview from "components/community/PostContentPreview";
import MediaCarousel from "components/community/MediaCarousel";
import useCommunityCategories from "hooks/useCommunityCategories";
import LoadingSpinner from "components/common/LoadingSpiner";
import SubHeader from "components/common/SubHeader";
import { getMessagePaperPlaneIcon } from "assets/images/icons/messagePaperPlane";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import useRefreshCallback from "hooks/useRefreshCallback";
import { formatTime } from "utils/util";
import { getPublicUserKey } from "utils/userIdentity";
import "pages/community/community.scss";
import Seo from "components/common/Seo";

const messageIcon = getMessagePaperPlaneIcon('#464a4d');

// 피드 정렬 (ADR-0004 / Community PR #28).
// /feed 는 latest(최신) · foryou(인기) 만 지원한다. 기본값은 최신글(latest)이며,
// 다른 화면 갔다가 재진입하면 컴포넌트가 새로 마운트되어 이 기본값으로 리셋된다.
const DEFAULT_SORT = "latest";
const SORT_OPTIONS = [
    { value: "latest", label: "최신글" },
    { value: "foryou", label: "인기글" },
];

const CommunityList = () => {
    const navigate = useNavigate();
    const { showError } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);
    const { categoryMap, categoryFilters } = useCommunityCategories();
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    // 최초 진입 로딩만 전체 화면 스피너, 이후 탭 전환 로딩은 콘텐츠 영역에만 표시
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [sortBy, setSortBy] = useState(DEFAULT_SORT);
    const observerRef = useRef(null);
    const bottomRef = useRef(null);
    const categoryRowRef = useRef(null);
    // 정렬/카테고리 전환 시 in-flight 응답이 뒤늦게 도착해 덮어쓰는 race 방지용 토큰
    const loadIdRef = useRef(0);

    // 피드 데이터 불러오기
    const loadFeed = useCallback(
        async (category, sort, pageNum, append = false) => {
            const loadId = ++loadIdRef.current;
            try {
                if (append) {
                    setIsLoadingMore(true);
                } else {
                    setIsLoading(true);
                    setPosts([]);
                }
                const result = await getFeed(pageNum, 20, {
                    postCategory: category || undefined,
                    sort,
                });
                // 이 응답이 도착하기 전 더 최신 요청이 시작됐으면 무시 (race 방지)
                if (loadId !== loadIdRef.current) return;
                if (result.success) {
                    setPosts((prev) =>
                        append ? [...prev, ...result.data] : result.data
                    );
                    setTotalPages(result.meta?.totalPages || 0);
                }
            } catch (error) {
                if (loadId !== loadIdRef.current) return;
                console.error("피드 로딩 실패:", error);
                showError("게시글을 불러오지 못했습니다.");
            } finally {
                if (loadId === loadIdRef.current) {
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    setHasLoadedOnce(true);
                }
            }
        },
        [showError]
    );

    useEffect(() => {
        setPage(1);
        loadFeed(selectedCategory, sortBy, 1);
    }, [selectedCategory, sortBy, loadFeed]);

    // 카테고리 전환 시 선택된 칩을 가로 스크롤 영역 가운데로 정렬한다.
    // (좌우 맨 끝 칩은 브라우저가 스크롤 범위를 클램프해 자연스럽게 끝에 배치된다.)
    useEffect(() => {
        if (isLoading) return;
        const activeChip =
            categoryRowRef.current?.querySelector(".category-chip.active");
        try {
            activeChip?.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        } catch (_) {
            // scrollIntoView 미구현 환경(jsdom 등) 방어
        }
    }, [selectedCategory, isLoading]);

    // 다른 탭(작성 창)에서 게시글 작성 완료 시 1페이지부터 새로 로드
    useEffect(() => {
        let channel;
        try {
            channel = new BroadcastChannel("community-events");
            channel.onmessage = (e) => {
                if (e?.data?.type === "post-created") {
                    setPage(1);
                    loadFeed(selectedCategory, sortBy, 1);
                }
            };
        } catch (_) {
            // BroadcastChannel 미지원 환경 무시
        }
        return () => {
            if (channel) channel.close();
        };
    }, [selectedCategory, sortBy, loadFeed]);

    // Pull-to-Refresh
    useRefreshCallback(() => {
        setPage(1);
        return loadFeed(selectedCategory, sortBy, 1);
    }, [selectedCategory, sortBy]);

    // 무한 스크롤
    useEffect(() => {
        if (!bottomRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (
                    entry.isIntersecting &&
                    !isLoadingMore &&
                    page < totalPages
                ) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadFeed(selectedCategory, sortBy, nextPage, true);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(bottomRef.current);
        observerRef.current = observer;
        return () => observer.disconnect();
    }, [page, totalPages, isLoadingMore, loadFeed, selectedCategory, sortBy]);

    // 좋아요 토글 (낙관적 업데이트)
    const handleLikeToggle = useCallback(
        async (e, postId) => {
            e.stopPropagation();
            if (!userInfo) {
                navigate('/login');
                return;
            }
            setPosts((prev) =>
                prev.map((p) =>
                    p.id === postId
                        ? {
                              ...p,
                              is_liked: !p.is_liked,
                              like_count: p.is_liked
                                  ? p.like_count - 1
                                  : p.like_count + 1,
                          }
                        : p
                )
            );
            try {
                await togglePostLike(postId);
            } catch (error) {
                // 실패 시 롤백
                setPosts((prev) =>
                    prev.map((p) =>
                        p.id === postId
                            ? {
                                  ...p,
                                  is_liked: !p.is_liked,
                                  like_count: p.is_liked
                                      ? p.like_count - 1
                                      : p.like_count + 1,
                              }
                            : p
                    )
                );
            }
        },
        [userInfo, navigate]
    );

    // 최초 진입 시에는 전체 화면 스피너 (헤더/탭 컨텍스트가 아직 없으므로)
    if (isLoading && !hasLoadedOnce) {
        return <LoadingSpinner />;
    }

    return (
        <div className="community-page community-list">
            <Seo
                title="커뮤니티"
                description="다이버들이 다이빙 후기·사진·정보를 공유하는 커뮤니티입니다. 프리다이빙과 스쿠버다이빙 이야기를 나눠보세요."
            />
            <SubHeader
                title="커뮤니티"
                goback={() => navigate("/")}
                menus={[{
                    name: "검색",
                    icon: `data:image/svg+xml,${encodeURIComponent('<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9" cy="9" r="6" stroke="#464a4d" stroke-width="1.5"/><path d="M13.5 13.5L17 17" stroke="#464a4d" stroke-width="1.5" stroke-linecap="round"/></svg>')}`,
                    callback: () => navigate("/community/search"),
                },
                ...(MESSAGING_ENABLED ? [{
                    name: "메시지",
                    icon: messageIcon,
                    callback: () => navigate("/messages"),
                }] : [])]}
            />

            <div className="list-sticky-header">
                <div className="category-filter" ref={categoryRowRef}>
                    {categoryFilters.map((cat) => (
                        <button
                            key={cat.value}
                            className={`category-chip${selectedCategory === cat.value ? " active" : ""}`}
                            onClick={() => setSelectedCategory(cat.value)}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                <div className="sort-tabs" role="tablist" aria-label="게시글 정렬">
                    {SORT_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            role="tab"
                            className={`sort-tab${sortBy === opt.value ? " active" : ""}`}
                            aria-selected={sortBy === opt.value}
                            onClick={() => setSortBy(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                // 탭 전환 후 재조회 로딩: 헤더·탭은 유지한 채 콘텐츠 영역에만 스피너 표시
                <div className="feed-loading" role="status" aria-label="로딩 중">
                    <div className="loading-spinner">
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                    </div>
                </div>
            ) : posts.length === 0 ? (
                <div className="empty-state">
                    <h3 className="empty-title">아직 게시글이 없어요</h3>
                    <p className="empty-description">
                        첫 번째 게시글을 작성해보세요!
                    </p>
                </div>
            ) : (
                <div className="post-list">
                    {posts.map((post) => (
                        <article
                            key={post.id}
                            className="post-card"
                            onClick={() =>{ 
                              // navigate(`/community/${post.id}`)
                              window.open(`/community/${post.id}`, "_blank");
                            }
                            }
                        >
                            {/* 작성자 행 */}
                            <div className="author-row">
                                {post.author_profile_image ? (
                                    <img
                                        src={post.author_profile_image}
                                        alt=""
                                        className="author-avatar clickable"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank");
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="author-avatar-placeholder clickable"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank");
                                        }}
                                    >
                                        {(
                                            post.author_nickname ||
                                            post.author_name ||
                                            "?"
                                        ).charAt(0)}
                                    </div>
                                )}
                                <div className="author-info">
                                    <div className="author-name-row">
                                        <span
                                            className="author-nickname clickable"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank");
                                            }}
                                        >
                                            {post.author_nickname ||
                                                post.author_name}
                                        </span>
                                        {post.post_category && (
                                            <>
                                                <span className="separator">
                                                    ›
                                                </span>
                                                <span className="activity-type">
                                                    {categoryMap[post.post_category] || post.post_category}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <span className="post-time">
                                        {formatTime(post.created_at)}
                                    </span>
                                </div>
                            </div>

                            {/* 본문 */}
                            <PostContentPreview content={post.content} />

                            {/* 이미지 */}
                            <MediaCarousel
                                mediaUrls={post.media_urls}
                            />

                            {/* 액션 바 */}
                            <div className="action-bar">
                                <button
                                    className="action-item"
                                    onClick={(e) =>
                                        handleLikeToggle(e, post.id)
                                    }
                                >
                                    <svg
                                        className="action-icon"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M10 17.5s-7-4.5-7-9.5A4 4 0 0 1 10 5a4 4 0 0 1 7 3c0 5-7 9.5-7 9.5z"
                                            fill={
                                                post.is_liked
                                                    ? "#e40000"
                                                    : "none"
                                            }
                                            stroke={
                                                post.is_liked
                                                    ? "#e40000"
                                                    : "#464a4d"
                                            }
                                            strokeWidth="1.5"
                                        />
                                    </svg>
                                    {post.like_count > 0 && <span className="action-count">
                                        {post.like_count}
                                    </span>}
                                </button>
                                <button
                                    className="action-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                            `/community/${post.id}`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="action-icon"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M17 10c0 3.866-3.134 7-7 7a7.2 7.2 0 0 1-3.5-.9L3 17l.9-3.5A7.2 7.2 0 0 1 3 10c0-3.866 3.134-7 7-7s7 3.134 7 7z"
                                            stroke="#464a4d"
                                            strokeWidth="1.5"
                                            fill="none"
                                        />
                                    </svg>
                                    {post.comment_count > 0 && <span className="action-count">
                                        {post.comment_count}
                                    </span>}
                                </button>
                            </div>
                        </article>
                    ))}
                    <div ref={bottomRef} />
                    {isLoadingMore && (
                        <div className="loading-more">로딩 중...</div>
                    )}
                </div>
            )}

            {createPortal(
                <div className="fab-wrapper">
                    <div className="fab-container">
                        <button
                            className="fab-button"
                            onClick={() => userInfo ? window.open("/community/create", "_blank") : navigate("/login")}
                            aria-label="게시글 작성"
                        >
                            +
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CommunityList;
